import styles from './Profile.module.css';

import classNames from 'classnames';
import { useNavigate } from 'react-router';
import React, { useState, useEffect } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';

import { useUserSession } from 'src/hooks/useUserSession';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';

import { leaveSiteFor } from 'src/utils/leaveSite';
import { isTokenRejected } from 'src/utils/requestError';
import { dealerProfileUrl } from 'src/utils/dealerProfile';
import { clearAllSessions } from 'src/utils/storefrontSession';

import { CONFIG } from 'src/config-global';
import { WalletButton, isChainFamily, useDisconnectWallets } from 'src/blockchain';
import {
  QueryKeys,
  useLogout,
  useXpBalance,
  useAccountWallet,
  useLogoutEverywhere,
} from 'src/queries';

import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';

import {
  XpBalanceCard,
  AccountEmailCard,
  NotificationsCard,
  XpTransactionItem,
  LinkedWalletsCard,
  CurrencyBalanceCard,
} from './components';

/** How old the balance on `/wallet/auth/me` may be when the profile opens before it is re-asked. */
const BALANCE_MAX_AGE_MS = 30 * 1000;

/**
 * Profile, by who is signed in (app#370). A dealer's Profile is their profile in the dealer cabinet,
 * so this address — whichever way it is reached: the tab, the address bar, "Link a wallet" at Buy /
 * Book and on the deal lists — sends a dealer there. A buyer gets the account's screen below, and so
 * does a visitor, with its sign-in card. The role is the account's (`/user/auth/me`): until the api
 * has named it, the screen is the visitor's.
 */
export const Profile = () => {
  const { account } = useUserSession();
  const dealerProfile = dealerProfileUrl(account?.role, CONFIG.dealerCabinetUrl);

  return dealerProfile ? <ToDealerProfile url={dealerProfile} /> : <AccountProfile />;
};

/**
 * On the way to the dealer cabinet. The cabinet's address replaces this one (`leaveSiteFor`), so
 * Back from the cabinet returns to the page Profile was pressed on — not to `/profile`, which would
 * send the dealer straight back. The buyer's screen is never drawn for a dealer, and nothing of it
 * is asked: no wallet, no XP.
 */
const ToDealerProfile = ({ url }: { url: string }) => {
  useDocumentTitle('Dealer profile');

  useEffect(() => {
    leaveSiteFor(url);
  }, [url]);

  return (
    <div className={classNames('container', styles.container)}>
      <Stack width="100%" gap={4} p={4} pb={30} maxWidth="750px" alignItems="center">
        <Loader />
        <Typography as="h1" variant="title3" fontWeight="600" color="white" textAlign="center">
          Opening your dealer profile...
        </Typography>
        {/* The same address as a plain link, for a page that is slow to come: pressed, or opened in a
            new tab, it goes where the screen is already going. */}
        <a className={styles.cabinetLink} href={url}>
          Open the dealer cabinet
        </a>
      </Stack>
    </div>
  );
};

/**
 * The account's screen. One session since the e-mail-only sign-in (api#402, step 2): the account,
 * read from `/user/auth/me`; its wallet — the primary one, which the api resolves the session to
 * on every wallet-scoped route — is read from `/wallet/auth/me` under the same token, and is
 * what the balances, the XP ledger and the wallet at the top of the page belong to.
 */
const AccountProfile = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoggedIn, account } = useUserSession();
  useDocumentTitle('Account');

  // The first wallet an account links becomes its primary one for good (no unlink in api), so
  // "has a wallet" is this field — and the wallet-scoped reads below have nothing to ask without it:
  // the api answers 403 WALLET_NOT_LINKED (app#155, app#241).
  const hasWallet = !!account?.primaryWalletId;

  // The wallet at the top of the page is a wallet this person already holds, so its family is
  // read, not assumed (app#243): the account's primary wallet — the same `/wallet/auth/me` answer
  // the email-change card signs against, from the same cache. It used to be TRON for everyone,
  // which put a TRON "Select Wallet" at the top of the profile of a buyer whose only wallet is
  // MetaMask. An account with no wallet at all has nothing to show here: its choice of family is
  // the `Linked wallets` card below, one slot each.
  const { data: accountWallet } = useAccountWallet(isLoggedIn && hasWallet);
  const walletFamily = isChainFamily(accountWallet?.chainType)
    ? accountWallet.chainType
    : undefined;
  // XP belongs to the wallet: asked only for an account that has one, not for a visitor or an
  // account that has not linked a wallet yet (app#155). Without one the section simply is not there.
  const { data: xpData, isLoading: isLoadingXp } = useXpBalance({
    enabled: isLoggedIn && hasWallet,
  });

  // `/wallet/auth/me` is identity, and identity may stand (staleness.ts) — but the same answer
  // carries the wallet's live balance, and this is the screen that shows it. Refreshed in the
  // background when the answer is older than the balance may be, so a payment made a minute ago
  // is on the card — and not re-asked on a cold load, where it was fetched a moment ago.
  useEffect(() => {
    if (!isLoggedIn || !hasWallet) return;
    queryClient.invalidateQueries({
      queryKey: QueryKeys.accountWallet(),
      predicate: (query) => Date.now() - query.state.dataUpdatedAt > BALANCE_MAX_AGE_MS,
    });
  }, [isLoggedIn, hasWallet, queryClient]);

  const isLoading = isLoadingXp;

  return (
    <div className={classNames('container', styles.container)}>
      <Stack width="100%" gap={4} p={4} pb={30} maxWidth="750px">
        {walletFamily ? (
          <Stack width="100%" alignItems="center" pt={4}>
            <WalletButton family={walletFamily} />
          </Stack>
        ) : null}

        <AccountEmailCard />

        <LinkedWalletsCard />

        <NotificationsCard />

        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className={styles.balanceCardsGrid}>
              {xpData && <XpBalanceCard balance={xpData.balance} />}
              {accountWallet?.balance?.map((currency) => (
                <CurrencyBalanceCard
                  key={currency.symbol}
                  balance={currency.balance}
                  symbol={currency.symbol}
                  logoURI={currency.logoURI}
                  name={currency.name}
                />
              ))}
            </div>

            {xpData && (
              <Stack gap={2}>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  pl={4}
                >
                  <Typography fontWeight="600" variant="title3" color="white">
                    Recent Transactions
                  </Typography>
                  <Button
                    onClick={() => navigate('/xp-transactions')}
                    title="View All"
                    variant="transparent"
                    textColor="white"
                    iconLeft={<ArrowRight size={20} color="white" />}
                  />
                </Stack>

                {xpData.transactions && xpData.transactions.length > 0 ? (
                  <Stack gap={2}>
                    {xpData.transactions.map((transaction) => (
                      <XpTransactionItem key={transaction.id} transaction={transaction} />
                    ))}
                  </Stack>
                ) : (
                  <Stack gap={2} alignItems="center" p={4}>
                    <Typography color="lightWhite" textAlign="center">
                      No transactions yet
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </>
        )}

        {isLoggedIn ? <SignOutButtons /> : null}
      </Stack>
    </div>
  );
};

/**
 * The way out — two of them since api#402 (step 1 gave the api several sessions per account, one
 * per device):
 *
 * - **Sign out** ends this device's session (`POST /user/auth/logout` names the session by the
 *   token that calls it); the phone, the laptop and the other tabs stay signed in.
 * - **Sign out everywhere** ends every session of the account (`POST /user/auth/logout/all`) — the
 *   one to press on a device that is lost, or after signing in on a shared one and forgetting.
 *
 * Either way the session goes, then the page is reloaded rather than re-rendered: the query cache
 * holds the previous person's deals, so a reset from empty storage is the only way to leave
 * nothing of them behind — which is the whole point of signing out. A refused `Sign out` still
 * ends the session here: the server should forget it too, but a failure there must not strand
 * the person in a session they asked to leave. `Sign out everywhere` is a promise about the other
 * devices, so a refusal there is said, and the session stays until it is kept.
 *
 * The wallets go too (app#183). A connected wallet is not a session — nothing is signed in with a
 * wallet any more — but it is the previous person's key, still authorised for this site and one
 * click away from paying; on a shared device "Sign out" that leaves it connected has not done what
 * it says. Every family this build carries is disconnected. The reload does not wait for a slow
 * adapter, though: the tokens are gone by then, and the reload is the one step that must happen.
 */
const DISCONNECT_WAIT_MS = 3000;

const SignOutButtons = () => {
  const { mutateAsync: logout } = useLogout();
  const { mutateAsync: logoutEverywhere } = useLogoutEverywhere();
  const disconnectWallets = useDisconnectWallets();
  const [busy, setBusy] = useState<'here' | 'everywhere' | null>(null);
  const [error, setError] = useState('');

  const leave = async () => {
    clearAllSessions();
    await Promise.race([
      disconnectWallets(),
      new Promise((resolve) => setTimeout(resolve, DISCONNECT_WAIT_MS)),
    ]);
    window.location.assign('/');
  };

  const handleSignOut = async () => {
    if (busy) return;
    setBusy('here');
    setError('');
    try {
      await logout();
    } catch {
      // The api could not take the sign-out (no network, a 5xx): the cookie is still in the browser,
      // and a tab that merely forgot it would be signed straight back in by its next `/me`. Said,
      // not pretended; a cookie the api refused is a session already over, and `useLogout` takes it
      // as done.
      setBusy(null);
      setError('Could not sign out. Check your connection and try again.');
      return;
    }
    await leave();
  };

  const handleSignOutEverywhere = async () => {
    if (busy) return;
    setBusy('everywhere');
    setError('');
    try {
      await logoutEverywhere();
    } catch (err) {
      // The api refused the token itself (401/403): the session was already ended — from another
      // device, or it expired — and there is nothing left to sign out of but this browser.
      if (!isTokenRejected(err)) {
        setBusy(null);
        setError('Could not sign out everywhere. Check your connection and try again.');
        return;
      }
    }
    await leave();
  };

  return (
    <Stack gap={2} alignItems="center">
      <Button
        title={busy === 'here' ? 'Signing out...' : 'Sign out'}
        variant="transparent"
        textColor="white"
        onClick={handleSignOut}
        disabled={busy !== null}
      />
      <Button
        title={busy === 'everywhere' ? 'Signing out everywhere...' : 'Sign out everywhere'}
        variant="transparent"
        textColor="lightWhite"
        onClick={handleSignOutEverywhere}
        disabled={busy !== null}
      />
      {error ? (
        <Typography color="danger" variant="small" textAlign="center">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
};
